import { FC, useCallback } from 'react';
import { SubmitHandler, useForm } from 'react-hook-form';

import TextInput from 'components/forms/inputs/rhf-inputs/TextInput';
import { min } from 'components/forms/inputs/rhf-validators/validators';
import { ModalFooter } from 'components/modals/ModalFooter';
import { ModalHeader } from 'components/modals/ModalHeader';
import { useAddSystemRoleMutation } from 'store/api/systemRoles';
import { CreateSystemRoleDto, SystemRole } from 'types/systemRoles';
import { t } from 'utils/getTranslations';

interface Props {
    onClose: () => void;
    onSave: (systemRole: SystemRole) => void;
}

const AddNewSystemRoleFormModal: FC<Props> = ({ onSave, onClose }) => {
    const { control, handleSubmit } = useForm<CreateSystemRoleDto>({
        defaultValues: {
            name: '',
        },
    });

    const [addSystemRole] = useAddSystemRoleMutation();

    const handleSave = useCallback<SubmitHandler<CreateSystemRoleDto>>(
        async (data) => {
            await addSystemRole(data)
                .unwrap()
                .then((systemRole) => {
                    onSave?.(systemRole);
                    onClose();
                });
        },
        [onSave, addSystemRole, onClose]
    );

    return (
        <>
            <ModalHeader title={t('roles-and-permissions.add-new-role')} onCancel={onClose} />
            <div className="overflow-y-auto p-6 max-sm:px-4">
                <TextInput
                    control={control}
                    name="name"
                    label="roles-and-permissions.role-name"
                    placeholder="forms.enter-role-name"
                    maxLength={30}
                    required
                    validate={{ minLength: (value) => min(value, 3) }}
                    pattern={/^[\p{Script=Latin}\p{M}0-9\s'_ -]+$/u}
                    autoFocus
                />
            </div>
            <ModalFooter
                onSave={() => {
                    void handleSubmit(handleSave)();
                }}
                onCancel={onClose}
                saveButtonTextId="add"
            />
        </>
    );
};

export default AddNewSystemRoleFormModal;
